import {
    Box,
    FormLabel,
    Grid,
    GridItem,
    Input,
    Select,
    Text,
    Badge,
    HStack,
    VStack,
    Divider,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { putApi } from "common/services/api";
import { editWorkflowSchema } from "common/schema/validationAddSchema";
import { workflowOptions } from "common/constant";
import moment from "moment";
import { getModuleDisplayName } from "common/utils/CalcFunctions";

const EditRules = (props) => {
    const {
        fetchData,
        data,
        viewMode = false,
        permission,
        selectedId,
        provideFormikConfig = () => { }
    } = props;
    console.log(props, "see props")
    const [isLoading, setIsLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);

    const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
            setShowDropdown(false);
        }
    };
    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const initialValues = {
        name: data?.name || "",
        module: data?.module || "",
        triggerType: data?.trigger?.type || "",
        isActive: typeof data?.isActive === "boolean" ? data?.isActive : false,
        executionCount: data?.executionCount || 0,
        priority: data?.priority || 10,
        description: data?.description || "",
        version: data?.version || 1
    }

    const { formik } = props;

    useEffect(() => {
        if (!provideFormikConfig) return;

        const config = {
            initialValues,
            validationSchema: editWorkflowSchema,
            onSubmit: async (values, { resetForm }) => {
                await EditData(values);
                resetForm();
            },
        };

        provideFormikConfig(config);

    }, [data]);

    const EditData = async (values) => {
        try {
            setIsLoading(true);
            const updatedFields = {};

            if (values.name !== initialValues.name)
                updatedFields.name = values.name;
            if (values.module !== initialValues.module)
                updatedFields.module = values.module;
            if (values.triggerType !== initialValues.triggerType)
                updatedFields.trigger = { type: values.triggerType };
            if (values.priority !== initialValues.priority)
                updatedFields.priority = values.priority;
            if (values.description !== initialValues.description)
                updatedFields.description = values.description;
            if (values.isActive !== initialValues.isActive)
                updatedFields.isActive = values.isActive;

            const response = await putApi(`api/workflows/${selectedId}`, updatedFields);

            if (response && response.status === 200) {
                toast.success("Workflow rule updated successfully");
                fetchData();
            } else {
                toast.error(response.response?.data?.message || "Update failed.");
            }
        } catch (e) {
            toast.error("Something went wrong");
        } finally {
            setIsLoading(false);
        }
    };



    const getTriggerDisplayName = (triggerType) => {
        const trigger = workflowOptions?.eventOptions?.find(option => option.value === triggerType);
        return trigger?.label || triggerType;
    };

    const formatConditions = (conditions) => {
        if (!conditions || conditions.length === 0) return "Apply to all records";

        return conditions.map((condition, index) => {
            const connector = index > 0 ? ` ${condition.logicalOperator || 'AND'} ` : '';
            return `${connector}${condition.field} ${condition.operator} ${condition.value}`;
        }).join('');
    };

    const getActionSummary = (actions) => {
        if (!actions || actions.length === 0) return "No actions configured";

        return actions.map((action, index) => {
            switch (action.type) {
                case 'field_update':
                    const updates = action.fieldUpdateConfig?.updates || [];
                    return updates.map(update =>
                        `Set ${update.field} to ${update.value}`
                    ).join(', ');
                case 'email_notification':
                    return "Send email notification";
                case 'task_creation':
                    return "Create task";
                default:
                    return action.type;
            }
        }).join('; ');
    };

    return (
        <form onSubmit={formik?.handleSubmit}>
            <Box className="manualFormContainer test">
                <Box className="manualFormBody">
                    {/* Workflow Summary Section */}
                    <Box mb={6} p={4} bg="gray.50" borderRadius="md" border="1px solid #e2e8f0">
                        <Text fontSize="md" fontWeight="600" mb={3}>Workflow Summary</Text>

                        <Grid templateColumns="repeat(12, 1fr)" gap={3} mb={4}>
                            <GridItem colSpan={{ base: 12, md: 3 }}>
                                <VStack align="start" spacing={1}>
                                    <Text fontSize="xs" color="gray.500" fontWeight="500">STATUS</Text>
                                    <Badge
                                        colorScheme={data?.isActive ? "green" : "red"}
                                        variant="solid"
                                        fontSize="xs"
                                    >
                                        {data?.isActive ? 'Active' : 'Inactive'}
                                    </Badge>
                                </VStack>
                            </GridItem>

                            <GridItem colSpan={{ base: 12, md: 3 }}>
                                <VStack align="start" spacing={1}>
                                    <Text fontSize="xs" color="gray.500" fontWeight="500">EXECUTIONS</Text>
                                    <Text fontSize="sm" fontWeight="600">{data?.executionCount || 0}</Text>
                                    {data?.lastExecuted && (
                                        <Text fontSize="xs" color="gray.500">
                                            Last: {moment(data.lastExecuted).format('MMM DD, h:mm A')}
                                        </Text>
                                    )}
                                </VStack>
                            </GridItem>

                            <GridItem colSpan={{ base: 12, md: 3 }}>
                                <VStack align="start" spacing={1}>
                                    <Text fontSize="xs" color="gray.500" fontWeight="500">MODULE</Text>
                                    <Text fontSize="sm" fontWeight="600">{getModuleDisplayName(data?.module)}</Text>
                                </VStack>
                            </GridItem>

                            <GridItem colSpan={{ base: 12, md: 3 }}>
                                <VStack align="start" spacing={1}>
                                    <Text fontSize="xs" color="gray.500" fontWeight="500">TRIGGER</Text>
                                    <Text fontSize="sm" fontWeight="600">{getTriggerDisplayName(data?.trigger?.type)}</Text>
                                </VStack>
                            </GridItem>
                        </Grid>

                        <Divider mb={3} />

                        <Grid templateColumns="repeat(12, 1fr)" gap={3}>
                            <GridItem colSpan={{ base: 12, md: 6 }}>
                                <VStack align="start" spacing={1}>
                                    <Text fontSize="xs" color="gray.500" fontWeight="500">CONDITIONS</Text>
                                    <Text fontSize="sm">
                                        {data?.targetCriteria?.applyToAll
                                            ? "Apply to all records"
                                            : formatConditions(data?.targetCriteria?.conditions)
                                        }
                                    </Text>
                                </VStack>
                            </GridItem>

                            <GridItem colSpan={{ base: 12, md: 6 }}>
                                <VStack align="start" spacing={1}>
                                    <Text fontSize="xs" color="gray.500" fontWeight="500">ACTIONS</Text>
                                    <Text fontSize="sm">{getActionSummary(data?.actions)}</Text>
                                </VStack>
                            </GridItem>
                        </Grid>
                    </Box>

                    {/* Original Form Fields */}
                    <Grid templateColumns="repeat(12, 1fr)" gap={3}>
                        {/* Rule Name */}
                        <GridItem colSpan={{ base: 6 }}>
                            <FormLabel display="flex" alignItems="center" fontSize="sm" fontWeight="500" mb="8px" gap="1">
                                Rule Name<Text color="red">*</Text>
                            </FormLabel>
                            <Input
                                disabled={viewMode}
                                fontSize="sm"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.name}
                                name="name"
                                placeholder="Enter Rule Name"
                                fontWeight="500"
                                borderColor={formik.errors.name && formik.touched.name ? "red.300" : undefined}
                            />
                            <Text mb="10px" color="red">
                                {formik.errors.name && formik.touched.name && formik.errors.name}
                            </Text>
                        </GridItem>

                        <GridItem colSpan={{ base: 6 }}>
                            <FormLabel display="flex" alignItems="center" fontSize="sm" fontWeight="500" mb="8px" gap="1">
                                Rule Description<Text color="red">*</Text>
                            </FormLabel>
                            <Input
                                disabled={viewMode}
                                fontSize="sm"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.description}
                                name="description"
                                placeholder="Enter Rule Description"
                                fontWeight="500"
                                borderColor={formik.errors.description && formik.touched.description ? "red.300" : undefined}
                            />
                            <Text mb="10px" color="red">
                                {formik.errors.description && formik.touched.description && formik.errors.description}
                            </Text>
                        </GridItem>

                        {/* Module - Read only */}
                        <GridItem colSpan={{ base: 6 }}>
                            <FormLabel fontSize="sm" fontWeight="500" mb="8px">
                                Module
                            </FormLabel>
                            <Input
                                disabled={true}
                                fontSize="sm"
                                value={getModuleDisplayName(formik.values.module)}
                                placeholder="Module"
                                fontWeight="500"
                                bg="gray.100"
                            />
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                Module cannot be changed
                            </Text>
                        </GridItem>

                        {/* Trigger Type */}
                        <GridItem colSpan={{ base: 6 }}>
                            <FormLabel fontSize="sm" fontWeight="500" mb="8px">
                                Execute On
                            </FormLabel>
                            <Select
                                disabled={viewMode}
                                name="triggerType"
                                value={formik.values.triggerType}
                                onChange={formik.handleChange}
                                placeholder="Select execute on"
                            >
                                {workflowOptions?.eventOptions.map(({ value, label }) => (
                                    <option key={value} value={value}>
                                        {label}
                                    </option>
                                ))}
                            </Select>
                            <Text mb="10px" color="red">
                                {formik.errors.triggerType && formik.touched.triggerType && formik.errors.triggerType}
                            </Text>
                        </GridItem>

                        {/* Priority */}
                        <GridItem colSpan={{ base: 6 }}>
                            <FormLabel fontSize="sm" fontWeight="500" mb="8px">
                                Priority
                            </FormLabel>
                            <Input
                                disabled={viewMode}
                                type="number"
                                fontSize="sm"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.priority}
                                name="priority"
                                placeholder="Priority"
                                fontWeight="500"
                            />
                        </GridItem>

                        {/* Execution Count - Read only */}
                        <GridItem colSpan={{ base: 6 }}>
                            <FormLabel fontSize="sm" fontWeight="500" mb="8px">
                                Execution Count
                            </FormLabel>
                            <Input
                                disabled={true}
                                type="number"
                                fontSize="sm"
                                value={data?.executionCount ?? 0}
                                placeholder="Execution Count"
                                fontWeight="500"
                                bg="gray.100"
                            />
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                Total executions: {data?.executionCount || 0}
                            </Text>
                        </GridItem>

                        {/* Version - Read only */}
                        <GridItem colSpan={{ base: 6 }}>
                            <FormLabel fontSize="sm" fontWeight="500" mb="8px">
                                Version
                            </FormLabel>
                            <Input
                                disabled={true}
                                type="number"
                                fontSize="sm"
                                value={data?.version ?? 1}
                                placeholder="Version"
                                fontWeight="500"
                                bg="gray.100"
                            />
                        </GridItem>

                        {/* Status */}
                        <GridItem colSpan={{ base: 6 }}>
                            <FormLabel display="flex" alignItems="center" fontSize="sm" fontWeight="500" mb="8px" gap="1">
                                Status <Text as="span" color="red">*</Text>
                            </FormLabel>
                            <Select
                                disabled={viewMode}
                                fontSize="sm"
                                name="isActive"
                                value={formik.values.isActive ? "true" : "false"}
                                onChange={(e) => formik.setFieldValue("isActive", e.target.value === "true")}
                            >
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </Select>
                            <Text mb="10px" color="red">
                                {formik.errors.isActive && formik.touched.isActive && formik.errors.isActive}
                            </Text>
                        </GridItem>


                    </Grid>
                </Box>
            </Box>
        </form>
    );
};

export default EditRules;